import { useState, useEffect } from 'react'
import {
  EyeIcon,
  PencilSquareIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline'
import { Routes, Route, Link, useParams, useLocation } from 'react-router-dom'

import { useSidebar } from '@/components/ui/sidebar'
import { useEducatorProject } from './hooks'
import Content from './Content'
import Preview from './Preview'
import LessonNameModal from './LessonNameModal'
import { UnsavedChangesProvider } from '@contexts/unsavedChangesContext'
import Breadcrumbs from '@components/Breadcrumbs'

import { Button, buttonVariants } from '@components/ui/button'
import { cn } from '@/helpers/utils'
import { useBreakpoint } from '@contexts/breakpoints'
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@components/ui/dropdown-menu'

const EducatorProjectDetails = () => {
  const { id } = useParams()
  const { project, status, refetch } = useEducatorProject({ id })
  const location = useLocation()
  const [isLessonNameModalOpen, setIsLessonNameModalOpen] = useState(false)
  const { setOpen } = useSidebar()
  const isMobile = useBreakpoint('sm')

  useEffect(() => {
    setOpen(false)
  }, [])

  if (status === 'loading') return

  const projectName = project?.name || 'Untitled project'
  const seriesName = project?.series?.name || 'Untitled pathway'

  return (
    <UnsavedChangesProvider>
      <div className='relative flex flex-col h-full max-h-full overflow-hidden'>
        <Breadcrumbs>
          <If condition={!isMobile}>
            <Breadcrumbs.Link label='Pathways' to='/educators/courses' />
          </If>
          <Breadcrumbs.Link label={seriesName} to={`/educators/courses/${project.series.id}`} />
          <Breadcrumbs.Text label={projectName} />

          <button onClick={() => setIsLessonNameModalOpen(true)} className='rounded-full hover:bg-gray-200 p-1 ml-1'>
            <PencilSquareIcon className='size-5' />
          </button>
          <If condition={!isMobile}>
            <Choose>
              <When condition={location.pathname.endsWith('/preview')}>
                <Link to="." className={cn(buttonVariants(), "ml-auto flex gap-2 items-center")}>
                  Exit Preview<EyeIcon className="h-6 w-6" />
                </Link>
              </When>

              <Otherwise>
                <Link to="preview" className={cn(buttonVariants(), "ml-auto flex gap-2 items-center")}>
                  Preview<EyeIcon className="h-6 w-6" />
                </Link>
              </Otherwise>
            </Choose>
          </If>
          <Choose>
            <When condition={isMobile}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="ml-auto">
                    <EllipsisVerticalIcon className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <Choose>
                    <When condition={location.pathname.endsWith('/preview')}>
                      <DropdownMenuItem asChild>
                        <Link to="." className="flex gap-2 items-center">
                          <BookOpenIcon className="h-4 w-4 mr-2" />
                          Exit Preview
                        </Link>
                      </DropdownMenuItem>
                    </When>
                    <Otherwise>
                      <DropdownMenuItem asChild>
                        <Link to="preview" className="flex gap-2 items-center">
                          <EyeIcon className="h-4 w-4 mr-2" />
                          Preview
                        </Link>
                      </DropdownMenuItem>
                    </Otherwise>
                  </Choose>
                </DropdownMenuContent>
              </DropdownMenu>
            </When>
          </Choose>
        </Breadcrumbs>

        <Routes>
          <Route
            index
            element={
              <Content
                id={id}
                name={project.name}
                blocks={project.blocks}
                tutor={project.tutor}
                refetch={refetch}
                prompt={project.prompt}
                socraticModeEnabled={project.socraticModeEnabled}
                stemModeEnabled={project.stemModeEnabled}
                feedbackType={project.feedbackType}
                seriesId={project.series.id}
              />
            }
          />
          <Route

            path='/preview/*'
            element={<Preview id={id} />}
          />
        </Routes>

      </div>

      <LessonNameModal
        isOpen={isLessonNameModalOpen}
        closeModal={() => setIsLessonNameModalOpen(false)}
        id={id}
        name={project.name}
      />
    </UnsavedChangesProvider>
  )
}

export default EducatorProjectDetails
